import { heicTo } from 'heic-to';

/**
 * Check if a file is HEIC/HEIF format
 */
export const isHeicFile = (file: File): boolean =>
  file.type === 'image/heic' ||
  file.type === 'image/heif' ||
  file.name.toLowerCase().endsWith('.heic') ||
  file.name.toLowerCase().endsWith('.heif');

/**
 * Check if a file is AVIF format. iPhones y cámaras modernas exportan AVIF
 * y varios pipelines (thumbnails, ffmpeg server-side, previews) no lo
 * manejan bien todavía, así que lo convertimos a JPEG antes de subirlo.
 */
export const isAvifFile = (file: File): boolean =>
  file.type === 'image/avif' ||
  file.name.toLowerCase().endsWith('.avif');

/**
 * Convert a HEIC/HEIF file to JPEG. Returns { file, url } where url is a blob URL for preview.
 * For non-HEIC files, returns the original file with a blob URL.
 */
export const convertHeicToJpeg = async (
  file: File,
  quality = 0.85
): Promise<{ file: File; url: string }> => {
  if (!isHeicFile(file)) {
    return { file, url: URL.createObjectURL(file) };
  }
  const jpegBlob = await heicTo({ blob: file, type: 'image/jpeg', quality });
  const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
  const newFile = new File([jpegBlob], newName, { type: 'image/jpeg' });
  return { file: newFile, url: URL.createObjectURL(jpegBlob) };
};

/**
 * Convert an AVIF file to JPEG using the browser's native AVIF decoder via
 * an offscreen `<img>` + canvas. Chrome 85+, Safari 16+ y Firefox 93+
 * decodifican AVIF nativamente, así que esto cubre ~todo el parque actual.
 */
export const convertAvifToJpeg = async (
  file: File,
  quality = 0.85,
): Promise<{ file: File; url: string }> => {
  if (!isAvifFile(file)) {
    return { file, url: URL.createObjectURL(file) };
  }
  const srcUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('El navegador no pudo decodificar la imagen AVIF'));
      el.src = srcUrl;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No se pudo crear un contexto 2D para convertir la imagen');
    ctx.drawImage(img, 0, 0);
    const jpegBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        b => (b ? resolve(b) : reject(new Error('toBlob devolvió null'))),
        'image/jpeg',
        quality,
      );
    });
    const newName = file.name.replace(/\.avif$/i, '.jpg');
    const newFile = new File([jpegBlob], newName, { type: 'image/jpeg' });
    return { file: newFile, url: URL.createObjectURL(jpegBlob) };
  } finally {
    URL.revokeObjectURL(srcUrl);
  }
};

/**
 * Normaliza un archivo de imagen antes de subirlo: convierte HEIC/HEIF/AVIF
 * a JPEG y deja el resto intacto. Es un no-op para formatos que el backend
 * y el visor ya manejan (jpeg/png/webp/gif/svg). Pensado para usarse dentro
 * de los helpers de upload, así cada call-site no tiene que saber qué
 * formato aceptar.
 */
export const normalizeImageForUpload = async (file: File): Promise<File> => {
  if (isHeicFile(file)) {
    const { file: converted } = await convertHeicToJpeg(file, 0.85);
    return converted;
  }
  if (isAvifFile(file)) {
    const { file: converted } = await convertAvifToJpeg(file, 0.85);
    return converted;
  }
  return file;
};
